import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle, Euro, Shield, Clock, Star, MapPin, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { CTASection } from "@/components/site/CTASection";
import { SEOHead } from "@/components/SEOHead";
import { BreadcrumbSchema } from "@/components/SchemaOrg";
import calendrierWhiteIcon from "@/assets/icons/calendrier-white.webp";
import { cities } from "@/data/citiesData";
import facadeCentreEte from "@/assets/centre/facade-centre-ete.webp";

const tarifs = [
  { type: "Voiture essence / diesel", webPrice: "73€", originalPrice: "80€" },
  { type: "Hybride / Électrique", webPrice: "79€", originalPrice: "86€" },
  { type: "Utilitaire léger", webPrice: "79€", originalPrice: "86€" },
  { type: "4x4 / GPL", webPrice: "88€", originalPrice: "95€" },
  { type: "Gros Volume / Camping-car", webPrice: "84€", originalPrice: "91€" },
  { type: "Véhicule de collection", webPrice: "73€", originalPrice: "80€" },
  { type: "Moto / Scooter (Cat. L)", webPrice: "70€", originalPrice: null },
  { type: "Voiturette (VSP)", webPrice: "70€", originalPrice: null },
  { type: "Visite Pollution", webPrice: "35€", originalPrice: null },
  { type: "Contre-visite (client centre)", webPrice: "30€", originalPrice: null },
  { type: "Contre-visite partielle", webPrice: "50€", originalPrice: null },
  { type: "Contre-visite complète", webPrice: "80€", originalPrice: null },
];

const economies = [
  {
    title: "Tarif préférentiel en ligne",
    description: "Réservez en ligne et bénéficiez automatiquement du tarif web, affiché directement sur chaque prestation.",
    highlight: true,
  },
  {
    title: "Pas de frais cachés",
    description: "Le tarif affiché est le tarif final. Aucun supplément surprise à la caisse.",
    highlight: false,
  },
  {
    title: "Contre-visite ciblée",
    description: "En cas de défaillance, la contre-visite ne porte que sur les points défaillants, pas un contrôle complet.",
    highlight: false,
  },
  {
    title: "Résultat immédiat",
    description: "Pas de second déplacement nécessaire : le procès-verbal est remis immédiatement après le contrôle.",
    highlight: false,
  },
];

const PasCher93Page = () => {
  return (
    <SiteLayout>
      <SEOHead
        title="CT Pas Cher 93 – Dès 73€"
        description="Contrôle technique pas cher en Seine-Saint-Denis : tarif web 73€ au lieu de 80€. Centre AUTOSUR agréé à Montreuil. RDV 24/7, parking gratuit."
        path="/controle-technique-pas-cher-93"
      />
      <BreadcrumbSchema items={[
        { name: "Accueil", href: "/" },
        { name: "Contrôle technique pas cher 93", href: "/controle-technique-pas-cher-93" }
      ]} />

      {/* Hero */}
      <section className="relative py-24 bg-gradient-to-br from-autosur-navy to-autosur-blue overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${facadeCentreEte})` }}
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-body mb-6 border border-white/20">
              <Euro className="w-4 h-4" />
              Tarifs compétitifs en Seine-Saint-Denis
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Contrôle Technique Pas Cher <span className="text-autosur-red">93</span>
            </h1>
            <p className="font-body text-white/80 text-lg mb-8">
              Des tarifs transparents et compétitifs pour votre contrôle technique en Seine-Saint-Denis. 
              Centre agréé Autosur à Montreuil-sous-Bois.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-autosur-red text-white font-heading font-semibold rounded-full hover:bg-autosur-red-light transition-all shadow-lg"
            >
              <img src={calendrierWhiteIcon} alt="" className="w-6 h-6" />
              Réserver en ligne
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Tarifs */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-autosur-navy mb-4">
              Nos tarifs
            </h2>
            <p className="font-body text-muted-foreground max-w-2xl mx-auto">
              Des prix clairs et compétitifs pour tous types de véhicules.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {tarifs.map((tarif, index) => (
              <motion.div
                key={tarif.type}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-autosur-blue/30 transition-all"
              >
                <p className="font-heading font-bold text-autosur-navy mb-2">{tarif.type}</p>
                <p className="font-heading text-2xl font-bold text-autosur-red">{tarif.webPrice}</p>
                {tarif.originalPrice ? (
                  <p className="font-body text-muted-foreground text-xs mt-1">
                    <span className="line-through">{tarif.originalPrice}</span> · Tarif web (*)
                  </p>
                ) : (
                  <p className="font-body text-muted-foreground text-xs mt-1">TTC</p>
                )}
              </motion.div>
            ))}
          </div>

          <p className="text-center font-body text-muted-foreground text-xs mt-6">
            (*) Offre non cumulable
          </p>
        </div>
      </section>

      {/* Économies */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
              Comment économiser sur votre contrôle technique
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {economies.map((eco, index) => (
              <motion.div
                key={eco.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`rounded-2xl p-6 shadow-sm border ${
                  eco.highlight 
                    ? "bg-autosur-blue/5 border-autosur-blue/20" 
                    : "bg-white border-gray-100"
                }`}
              >
                <h3 className="font-heading font-bold text-autosur-navy mb-2">{eco.title}</h3>
                <p className="font-body text-muted-foreground text-sm">{eco.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualité */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-6">
                Pas cher ne veut pas dire moins bien
              </h2>
              <div className="space-y-4 font-body text-muted-foreground">
                <p>
                  Chez <strong className="text-autosur-navy">Autosur Montreuil-sous-Bois</strong>, nous proposons 
                  des tarifs compétitifs sans compromettre la qualité. Notre centre est agréé par l'État et nos 
                  contrôleurs sont tous diplômés et formés en continu.
                </p>
                <p>
                  Autosur est la <strong className="text-autosur-navy">Marque Préférée des Français</strong> en 
                  contrôle technique, une reconnaissance qui témoigne de notre engagement qualité.
                </p>
              </div>

              <a
                href="https://trustville.com/avis-clients/pc/cyvO9g/controle_technique_/autosur_montreuil_sous_bois"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center gap-4 bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <div>
                  <p className="font-heading font-bold text-autosur-navy">4.9/5</p>
                  <p className="font-body text-muted-foreground text-xs">Avis clients vérifiés</p>
                </div>
              </a>

              <ul className="mt-6 space-y-3">
                {[
                  "Centre agréé par l'État",
                  "Équipements dernière génération",
                  "Plus de 20 ans d'expérience",
                  "Marque Préférée des Français",
                ].map(item => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="font-body text-autosur-navy text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={facadeCentreEte}
                  alt="Centre de contrôle technique Autosur Montreuil-sous-Bois - façade été"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Villes proches */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-autosur-navy mb-6 text-center">
            Nous desservons toute la Seine-Saint-Denis
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {cities.map(city => (
              <Link
                key={city.slug}
                to={`/${city.slug}`}
                className="inline-flex items-center gap-2 px-5 py-3 bg-white border border-gray-200 rounded-full font-body text-autosur-navy hover:border-autosur-blue/50 hover:bg-autosur-blue/5 transition-all text-sm"
              >
                <MapPin className="w-4 h-4 text-autosur-blue" />
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection 
        title="Contrôle technique pas cher dans le 93"
        subtitle="Réservez en ligne et profitez de nos tarifs préférentiels."
      />
    </SiteLayout>
  );
};

export default PasCher93Page;
